<div [ngBusy]="{busy: busy, message: ('common.pleaseWait' | translate)}">
  @if (deliveryAddresses?.length) {
    <div class="mb-4">
      <span class="text-pink pointer delivery-address"
            (click)="chooseDeliveryAddress()"
            [innerHTML]="'orderProcess.delivery.deliveryForms.chooseDeliveryAddress' | translate"
      >Choose a saved delivery address</span>
    </div>
  }

  <div class="delivery-to-door-container">
    <div class="form-group form-group-xl">
      <label [translate]="'common.delivery.deliveryAddress'"></label>

      <ui-input-loader-wrap [loading]="busyAddress">
        <ui-typeahead-address
          [countryCode]="countryCode"
          [placeholder]="'orderProcess.delivery.deliveryForms.deliveryAddressPlaceholder' | translate"
          [ngModel]="lookupAddress"
          (ngModelChange)="resetDeliveryState()"
          (selectItem)="onAddressSelected($event)"
          (busySearch)="busyAddress = $event"
        ></ui-typeahead-address>
      </ui-input-loader-wrap>

      <div class="text-muted font-xs mt-2">
        <span [translate]="'orderProcess.delivery.deliveryForms.addressSearchHint'"></span>
        <span [innerHTML]="deliveryNote"></span>
      </div>
    </div>
  </div>

  @if (geoLocationStatus === STATUS.MISSING_STREET_NUMBER) {
    <div class="text-danger"
         [translate]="'orderProcess.delivery.deliveryForms.missingStreetNumber'">
    </div>
  }

  @if (geoLocationStatus === STATUS.INVALID) {
    <div class="mb-3">
      <div class="text-danger"
           [translate]="'orderProcess.delivery.deliveryForms.notAvailable'">
      </div>

      @if (isAnotherDeliveryAvailable) {
        <div class="mt-3 font-md text-center text-color-default text-underline">
          <span [translate]="'orderProcess.delivery.deliveryForms.chooseAnotherDeliveryInstead'"
                (click)="onChooseAnotherDelivery.emit()">Pick up in store instead?</span>
        </div>
      }
    </div>
  }

  @if (deliveryError) {
    <ui-message class="d-block my-3"
                [type]="'error'"
                [title]="deliveryError.title">
      {{ deliveryError.message }}
    </ui-message>
  }

  @if (geoLocationStatus === STATUS.VALID) {
    @if (deliveryNoteForCustomer || availableProductTypes?.length) {
      <deliveries-notes
        class="d-block my-3"
        [deliveryNoteForCustomer]="deliveryNoteForCustomer"
        [availableProductTypes]="availableProductTypes">
      </deliveries-notes>
    }

    <daily-stock-sold-out-notice
      class="d-block my-3"
      [productTypes]="dailyStockSoldOutProductTypes"
    ></daily-stock-sold-out-notice>

    @if (asapFlowActivated) {
      <to-door-asap-choice class="d-block mb-3"
                           (pickLatter)="pickLatterTime()">
        <ng-container asap-time>
          {{ 'bakerySite.expressOrdering.common.todayAt' | translate: { time: asapTime } }}
        </ng-container>
        <ng-container asap-price>{{ asapPrice | currencyFormat: currency }}</ng-container>
      </to-door-asap-choice>
    }

    @if (fullCapacityDays?.length) {
      <sold-out-msg
        class="d-block my-3"
        [fullCapacityDays]="fullCapacityDays"
      ></sold-out-msg>
    }

    <form [formGroup]="deliveryForm" class="delivery-to-door-form">
      <ui-datetime-skeleton [loading]="skeletonPromise">
        @if (asapTime && asapPrice && !asapFlowActivated) {
          <div class="fw-bold font-xl mb-3 d-none d-md-block"
               [translate]="'bakerySite.expressOrdering.common.whenDelivered'">
            When should it be delivered?
          </div>

          <pickup-asap
            class="d-block mt-4 mb-2 mb-md-0"
            [time]="asapTime"
            [price]="(asapPrice | currencyFormat: currency)"
            [nextBtnView]="nextBtnView"
            (confirm)="selectAsapTime()"
          ></pickup-asap>
        }

        <div class="row"
             [class.d-none]="asapFlowActivated">
          <div class="col-6 col-lg-4">
            <div class="form-group form-group-xl delivery-datepicker-container">
              @if (!asapTime) {
                <label #datePickerLabel
                       [translate]="'common.delivery.deliveryDate'"></label>
              }

              <ui-delivery-datepicker
                [calendar]="calendar"
                [disabled]="isCalendarLoading"
                [formControlName]="'deliveryDate'"
                [maxDate]="maxDate"
                [placement]="'bottom-left'"
                (onMonthNavigate)="onMonthNavigate($event)"
                (onContextInit)="contextInit($event)"
              ></ui-delivery-datepicker>
            </div>
          </div>

          <div class="col-6 col-lg-4">
            <div class="form-group form-group-xl">
              @if (!asapTime) {
                <label [translate]="'orderProcess.delivery.deliveryForms.chooseTime'"></label>
              }

              <div class="dropdown-container dropdown-item-slim delivery-to-door-time">
                <ui-dropdown
                  #rangeDropDown
                  [placement]="'bottom'"
                  [items]="deliveryTimeItems"
                  [placeholder]="'common.timePlaceholder' | translate"
                  [formControlName]="'deliveryTime'"
                  (changeItem)="timeChanged($event)"
                ></ui-dropdown>
              </div>
            </div>
          </div>

          @if (showDeliveryPricesInfo && isPriceSet) {
            <div class="col-12 col-lg-4">
              <div class="form-group form-group-xl price-wrap"
                   [class.asap-view]="asapTime">
                <label class="price-label"
                       [translate]="'orderProcess.delivery.deliveryForms.deliveryPrice'">
                </label>

                <div class="price-nok">
                  {{ price | currencyFormat: currency }}
                </div>
              </div>
            </div>
          }
        </div>
      </ui-datetime-skeleton>

      @if (isDateTimeValid()) {
        <div class="mb-4">
          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.recipientName'"></label>

            <input type="text"
                   class="form-control"
                   [maxlength]="'128'"
                   [placeholder]="'common.name' | translate"
                   [formControlName]="'deliveryUserName'">
          </div>

          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.recipientPhone'"></label>

            @if (countryCode) {
              <ui-i18n-phone-input
                *libphonenumber
                [maxlength]="'32'"
                [defaultCountryCode]="countryCode"
                [showLabel]="false"
                [placeholder]="'common.telephone' | translate"
                [pattern]="PHONE_PATTERN"
                [formControlName]="'deliveryPhone'"
              ></ui-i18n-phone-input>
            }
          </div>

          <div class="form-group form-group-xl">
            <label [translate]="'orderProcess.delivery.deliveryForms.messageToDriver'"></label>

            <textarea
              class="form-control"
              rows="3"
              [maxlength]="'512'"
              [placeholder]="'orderProcess.delivery.deliveryForms.messageToDriverPlaceholder'| translate"
              [formControlName]="'deliveryNote'"
            ></textarea>
          </div>

          @if (possibleToRegister && deliveryAddresses === null && deliveryForm.valid) {
            <div class="delivery-address d-flex align-items-center mb-4 alert-circle-wrap">
              <i class="icon-alert-circle text-orange me-2 font-3xl"></i>

              <div>
                <span class="text-muted"
                      [translate]="'orderProcess.delivery.deliveryForms.signUpBenefit'">
                  Save delivery details for future purchases?</span>&nbsp;
                <a href="#" (click)="$event.preventDefault(); onRegister.emit()"
                   [translate]="'orderProcess.delivery.deliveryForms.signUp'"
                >Create an account</a>
              </div>
            </div>
          }

          @if (deliveryAddresses !== null && deliveryForm.valid && isCurrentAddressNotSaved()) {
            <div class="delivery-address d-flex align-items-center mb-4 alert-circle-wrap">
              <ui-checkbox [(model)]="saveAddressOnSubmit">
                <span [translate]="'orderProcess.delivery.deliveryForms.saveDeliveryAddress'">
                  Save delivery for next purchases
                </span>
              </ui-checkbox>
            </div>
          }

          <div class="confirm-btn-wrap">
            <button class="btn {{nextBtnView}} btn-mobile-full-width font-xl delivery-confirm-btn"
                    [disabled]="isNextBtnDisabled()"
                    (click)="confirmDelivery()">
              <span [translate]="nextBtnTextKey"></span>
            </button>
          </div>
        </div>
      }
    </form>
  }
</div>
